<script setup lang="ts">
import {computed, onErrorCaptured, ref, watch} from "vue";
import {selectedCell, drawerVisible, getIndicatorInfo, kdm, addIndicatorMetadata} from "@/store/kdm";
import {krrMatrixData, krr, loadKrr} from "@/store/krr";
import {useI18n} from "vue-i18n";
import {useFetch} from "@/composables/useFetch";
import CreateAndSearchNode from "@/components/CreateAndSearchNode.vue";
import CollectionBrowser from "@/components/CollectionBrowser.vue";

const { t } = useI18n()
const ofetch = useFetch()
const userCanEdit = (window as any).drupalSettings?.userCanEdit || false

// State: Search & Selection
const selectedIndicators = ref<string[]>([])
const saving = ref(false)

// Get existing indicator IDs for a cell from field_matrix_data
function getExistingIndicatorIds(cellId: string): string[] {
  if (!kdm.value?.field_matrix_data || !cellId) return []

  try {
    const matrixData = typeof kdm.value.field_matrix_data === 'string'
      ? JSON.parse(kdm.value.field_matrix_data)
      : kdm.value.field_matrix_data

    const cellData = matrixData[cellId]
    if (!cellData || typeof cellData !== 'object' || Array.isArray(cellData)) return []

    return Object.keys(cellData).filter(key => {
      const val = cellData[key]
      return val && typeof val === 'object' && ('sliderValue' in val || 'judgmentId' in val)
    })
  } catch (e) {
    console.error('Error parsing matrix data:', e)
    return []
  }
}

// Watch both drawerVisible AND selectedCell together
// This ensures we always have the correct pair of values
watch(
  [drawerVisible, selectedCell],
  ([visible, cellId]) => {
    if (visible && cellId) {
      selectedIndicators.value = [...getExistingIndicatorIds(cellId)]
    }
  },
  { immediate: true }
)

// Computed: Indicators to show in the "Available" section
const availableIndicators = computed(() => {
  if (kdm.value?.kdm_type === 1) {
    return [];
  }

  if (kdm.value?.kdm_type === 2) {
    return (kdm.value.iprs || []).map((ipr: any) => String(ipr.id));
  }

  // For Type 3, we only show indicators that are present in the underlying KRR cell
  const set = new Set<string>()
  if (krrMatrixData.value === null) return Array.from(set)
  const suffix = selectedCell.value.split('-').slice(-2).join('-')
  Object.keys(krrMatrixData.value).forEach(key => {
    if (key.endsWith(suffix)) {
      for (const id in krrMatrixData.value[key]) {
        set.add(id)
      }
    }
  })
  return Array.from(set)
})

const sortedAvailableIndicators = computed(() => {
  return availableIndicators.value
    .sort((a, b) => getIndicatorInfo(a).title.localeCompare(getIndicatorInfo(b).title))
})

// --- Logic: Save selected indicators directly to server ---
async function addLocal() {
  // Capture cell ID at start to ensure consistency
  const cellId = selectedCell.value

  if (!kdm.value || !cellId) return

  saving.value = true

  try {
    // Get current matrix data
    let matrixData: Record<string, any> = {}
    if (kdm.value.field_matrix_data) {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : { ...kdm.value.field_matrix_data }
    }

    // Get existing cell data — guard against corrupted array data.
    const rawCell = matrixData[cellId]
    const existingCellData = (!rawCell || Array.isArray(rawCell)) ? {} : rawCell
    const newCellData: Record<string, any> = {}

    // For each selected indicator, preserve existing data or create empty entry
    selectedIndicators.value.forEach(id => {
      const strId = String(id)
      if (existingCellData[strId]) {
        // Preserve existing sliderValue and judgmentId
        newCellData[strId] = existingCellData[strId]
      } else {
        // New indicator - empty values
        newCellData[strId] = { sliderValue: 0, judgmentId: '' }
      }
    })

    // Send PATCH to server with the captured cell ID
    const requestBody: Record<string, any> = {}
    requestBody[cellId] = newCellData

    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/`, {
      method: 'PATCH',
      body: requestBody,
    })

    // Update local store with response
    if (response.data) {
      kdm.value.field_matrix_data = response.data
    }

    drawerVisible.value = false
  } catch (err: any) {
    console.error("Failed to save indicators:", err)
    alert(`Error saving: ${err.data?.message || err.message || 'Unknown error'}`)
  } finally {
    saving.value = false
  }
}

// --- Logic: Node Created/Selected (from CreateAndSearchNode) ---
async function updateAndReload(nodeId: string, moderationClass?: string, href?: string, title?: string) {
  if (!kdm.value) return

  const info = {
    id: nodeId,
    title: title || getIndicatorInfo(nodeId).title,
    moderation_class: moderationClass || getIndicatorInfo(nodeId).moderation_class,
    href: href || getIndicatorInfo(nodeId).href,
    is_in_kik: true
  }

  // Update store metadata
  addIndicatorMetadata(info)

  // Add to selected indicators and save
  if (!selectedIndicators.value.includes(String(nodeId))) {
    selectedIndicators.value.push(String(nodeId))
  }

  await addLocal()
}

async function onNodeSelected(node: any) {
  const kikId = kdm.value?.kik?.kik_id || krr.value?.kik?.kik_id;
  const krrId = kdm.value?.krrId || krr.value?.nid;
  const cellId = selectedCell.value;

  try {
    const payload: Record<string, any> = {
      node_id: node.id,
      kik_id: kikId,
      cell_id: cellId,
    };
    if (krrId) payload.krr_id = krrId;

    const res = await ofetch<any>(`/epbapi/ipr/add-existing`, {
      method: 'POST',
      body: payload
    })
    await updateAndReload(res.id, res.moderation_class, res.href, res.title)
    // For type 3: reload KRR so availableIndicators stays in sync
    if (kdm.value?.kdm_type === 3 && krrId) {
      await loadKrr(String(krrId))
    }
  } catch(e: any) {
    console.error('Error in onNodeSelected:', e)
    alert("Error adding node: " + (e.message || e))
  }
}

async function onNodeCreated(node: any) {
  try {
    await updateAndReload(node.id, node.moderation_class, node.href, node.title)
    // For type 3: reload KRR so availableIndicators stays in sync
    const krrId = kdm.value?.krrId || krr.value?.nid
    if (kdm.value?.kdm_type === 3 && krrId) {
      await loadKrr(String(krrId))
    }
  } catch (e: any) {
    console.error('Error in onNodeCreated:', e)
    alert("Error adding node: " + (e.message || e))
  }
}

onErrorCaptured(() => {
  alert("Un problème de données empêche l'affichage du drawer")
})
</script>

<template>
  <div class="kdm-drawer-container d-flex flex-column h-100">
    <!-- Type 2 & 3: Uses KIK, can search in Cauldron -->
    <div class="mb-3" v-if="kdm?.kdm_type === 2 || kdm?.kdm_type === 3">
        <CreateAndSearchNode
            :user-can-edit="userCanEdit"
            node-type-label="Argument"
            create-endpoint="/epbapi/ipr"
            search-endpoint="/epbapi/matrixcollection/search-nodes?filter_by_group=1"
            :collection-id="kdm?.kik?.kik_id || krr?.kik?.kik_id"
            :creation-params="{
                kik_id: kdm?.kik?.kik_id || krr?.kik?.kik_id,
                cell_id: selectedCell,
                collection_id: kdm?.kik?.kik_id || krr?.kik?.kik_id,
                krr_id: kdm?.krrId
            }"
            @node-created="onNodeCreated"
            @node-selected="onNodeSelected"
        />
        <CollectionBrowser
            :user-can-edit="userCanEdit"
            :filter-by-group="true"
            @node-selected="onNodeSelected"
        />
    </div>

    <div class="section mb-4" v-if="kdm?.kdm_type !== 1">
      <h6 v-if="kdm?.kdm_type === 2">{{ t('epb.kdm.drawer.kik_elements') }}</h6>
      <h6 v-else>{{ t('epb.kdm.drawer.available') }}</h6>
      <div v-if="sortedAvailableIndicators.length === 0" class="text-muted small italic">
        {{ t('epb.kdm.drawer.none') }}
      </div>
      <div v-else class="kdm__indicators overflow-auto" style="max-height: 200px;">
        <template v-for="id in sortedAvailableIndicators" :key="id">
          <div class="form-check mb-1" :title="getIndicatorInfo(id).is_in_kik === false ? 'Indicator/Argument no longer contextualized in the KIK' : ''">
            <input
              class="form-check-input"
              type="checkbox"
              :value="id"
              :id="`ipr-${id}`"
              v-model="selectedIndicators"
              :disabled="saving"
            >
            <label class="form-check-label small" :for="`ipr-${id}`">
              <span :class="[getIndicatorInfo(id).moderation_class, getIndicatorInfo(id).is_in_kik === false ? 'fw-bold' : '']">{{ getIndicatorInfo(id).title }}</span>
            </label>
          </div>
        </template>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="addLocal"
          :disabled="saving"
        >
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ t('epb.kdm.drawer.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section h6 { font-size: 0.85rem; text-transform: uppercase; color: #666; margin-bottom: 0.75rem; font-weight: 600; }
.italic { font-style: italic; }
.x-small { font-size: 0.75rem; font-weight: 600; }
.search-results { background: #f8f9fa; }
.btn-xs { padding: 0.1rem 0.3rem; font-size: 0.75rem; }
</style>
